import type { BrowserOptions } from '@sentry/react';

/** The event `beforeSendTransaction` receives — `@sentry/react` does not re-export the type by name. */
export type SentryTransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

export const LCP_AFTER_TTFB_MEASUREMENT = 'lcp_after_ttfb';

/**
 * Add `lcp_after_ttfb` (LCP − TTFB, in ms) to pageload transactions so the client-side share of
 * LCP can be aggregated per event — `p75(measurements.lcp_after_ttfb)` — instead of as the
 * difference of two percentiles, which is not the same number. Anonymous surfaces are partly
 * TTFB-bound (Cloudflare challenge, cold Worker isolates), so their acceptance criteria use this
 * measurement (CUR-1053, task 0.3).
 *
 * Returns the event untouched when the transaction is not a pageload, either vital is missing,
 * negative or non-finite, or the difference is negative (bogus vitals must not become a data point).
 */
export function withLcpAfterTtfbMeasurement(event: SentryTransactionEvent): SentryTransactionEvent {
    if (event.contexts?.trace?.op !== 'pageload') {
        return event;
    }

    const lcp = event.measurements?.lcp;
    const ttfb = event.measurements?.ttfb;
    if (!lcp || !ttfb || !isNonNegativeFinite(lcp.value) || !isNonNegativeFinite(ttfb.value)) {
        return event;
    }

    const value = lcp.value - ttfb.value;
    if (value < 0) {
        return event;
    }

    event.measurements = {
        ...event.measurements,
        [LCP_AFTER_TTFB_MEASUREMENT]: { value, unit: 'millisecond' },
    };
    return event;
}

function isNonNegativeFinite(value: number): boolean {
    return value >= 0 && Number.isFinite(value);
}
